import * as React from 'react';
import * as MuiCore from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { Icon } from '../../../lib/icons';
import { isDataLoaded } from '../../../types/data-providers';
import { useDeviceData } from '../../../data/DataProviders/DeviceDataProvider';
import { DeviceChannelData } from '../../../types/device';
import { ManageAlarmsActions } from '../../../actions/actions/manage-alarms-actions';
import { useDispatch } from 'react-redux';
import { DataIndicators } from '../../DataIndicators';

type Props<TValue, TOptions> = {
    group: {
        name: string;
        id?: string;
        channelCount?: number;
        items?: TValue[];
    };
    component: React.FunctionComponent<{
        testId?: string;
        value: TValue;
        isLast: boolean;
        options?: TOptions;
        onClick?: () => void;
    }>;
    componentTestId?: string;
    options?: TOptions;
    style?: any;
    navigationFn?: (arg: TValue) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
    expand: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
}));

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function makeAlarmsListGroupComponent<TValue, TOptions = never>() {
    const component: React.FC<Props<TValue, TOptions>> = (props) => {
        const ListItemComponent = props.component;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classes = useStyles();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const dispatch = useDispatch();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [open, setOpen] = React.useState(false);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [id, setId] = React.useState('');
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const deviceData = useDeviceData();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [channelData, setChannelData] = React.useState<DeviceChannelData[]>([]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
            if (id !== '') dispatch(ManageAlarmsActions.SetSelectedDeviceId(id));
        }, [id]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
            if (isDataLoaded(deviceData) && isDataLoaded(deviceData.value.channelData)) {
                setChannelData(deviceData.value.channelData.value);
            }
        }, [deviceData]);

        return (
            <div data-testid="list-group">
                <MuiCore.Card style={{ textDecorationSkipInk: 'inherit', ...props.style }}>
                    <MuiCore.List
                        component="div"
                        disablePadding
                        subheader={
                            props.group.name ? (
                                <MuiCore.ListSubheader
                                    onClick={(event: any): void => {
                                        event.stopPropagation();
                                        setOpen(!open);
                                        setId(props.group.id || '');
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <MuiCore.ListItemText
                                        data-testid="list-group-title"
                                        primary={`${props.group.name} (${props.group.channelCount} Channels)`}
                                        primaryTypographyProps={{
                                            variant: 'subtitle2',
                                            color: 'primary',
                                            style: { fontWeight: 600, lineHeight: 3 },
                                        }}
                                    />

                                    <MuiCore.ListItemSecondaryAction>
                                        <MuiCore.IconButton>
                                            <Icon.ExpandMore
                                                className={`${classes.expand} ${open ? classes.expandOpen : ''}`}
                                            />
                                        </MuiCore.IconButton>
                                    </MuiCore.ListItemSecondaryAction>
                                </MuiCore.ListSubheader>
                            ) : (
                                undefined
                            )
                        }
                    >
                        <MuiCore.Divider />
                        <MuiCore.Collapse in={open} timeout="auto" unmountOnExit>
                            {!isDataLoaded(deviceData) && (
                                <DataIndicators
                                    errorMessage="Channel Data  not Found"
                                    emptyMessage="Channel Data"
                                    inline
                                    data={deviceData}
                                    emptyIcon="Devices"
                                    isEmpty={false}
                                    isDetailedInfo={false}
                                />
                            )}
                            {isDataLoaded(deviceData) && channelData.length > 0 && (
                                <MuiCore.List component="div" disablePadding>
                                    {channelData.map((item: any, listItemIndex) => (
                                        <div key={listItemIndex}>
                                            <ListItemComponent
                                                testId={props.componentTestId}
                                                value={item}
                                                isLast={listItemIndex + 1 === props.group.items?.length}
                                                options={props.options}
                                                onClick={(): void => props.navigationFn && props.navigationFn(item)}
                                            ></ListItemComponent>
                                        </div>
                                    ))}
                                </MuiCore.List>
                            )}
                        </MuiCore.Collapse>
                    </MuiCore.List>
                </MuiCore.Card>
            </div>
        );
    };

    return component;
}
